import { useEffect, useState } from 'react';
import { Copy, Loader2, Pencil, Plus, RefreshCw, Send, ShieldCheck, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../../../lib/supabase';
import { errorMessage, reportError } from '../../../../lib/errors';
import { useConfirm } from '../../../../components/ui/ConfirmProvider';
import { AdditionalWorkSheet } from './AdditionalWorkSheet';
import {
    money, requestActions, STATE_LABEL, STATE_TONE, visibleRequests,
    type AdditionalWorkRequest, type JobAuthorisation,
} from './additionalWork';

/**
 * What the mechanic is authorised to do on this job, at a glance:
 *
 *   Original approved work           £X
 *   Additional work #1   Accepted    £Y
 *   Additional work #2   Declined    £Z   (struck through)
 *   Additional work #3   Awaiting    £A
 *   Approved job total               £X + accepted only
 *   Job total now                    the job's lines today, when different
 *
 * Every figure comes from fn_get_job_authorisation; nothing is summed here.
 */
interface Props {
    jobId: string;
    orgId: string;
    /** Owner or mechanic: the only roles the database lets price work. */
    canPriceWork: boolean;
    /** Changes whenever the job's items change, so the panel re-reads. */
    refreshKey?: unknown;
    onChanged?: () => void;
}

const TONE_CLASS = {
    muted: 'bg-raised text-fg-3 border-line',
    amber: 'bg-gold-fill/10 text-gold border-gold/25',
    success: 'bg-success/10 text-success border-success/25',
    danger: 'bg-danger/10 text-danger border-danger/25',
} as const;

export function AdditionalWorkPanel({ jobId, orgId, canPriceWork, refreshKey, onChanged }: Props) {
    const confirm = useConfirm();
    const [auth, setAuth] = useState<JobAuthorisation | null>(null);
    const [loading, setLoading] = useState(true);
    const [sheet, setSheet] = useState<null | { mode: 'new' | 'edit' | 'revise'; request?: AdditionalWorkRequest }>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        let alive = true;
        supabase.rpc('fn_get_job_authorisation', { p_job_id: jobId }).then(({ data, error }) => {
            if (!alive) return;
            if (error) reportError(error, 'additional-work-panel.fn-get-job-authorisation');
            setAuth((data as JobAuthorisation | null) ?? null);
            setLoading(false);
        });
        return () => { alive = false; };
    }, [jobId, refreshKey, reloadTick]);

    const changed = () => { setReloadTick((n) => n + 1); onChanged?.(); };

    const send = async (r: AdditionalWorkRequest) => {
        setBusyId(r.id);
        try {
            const { data, error } = await supabase.functions.invoke('send-quote-email', { body: { quote_id: r.id } });
            const bodyError = (data as { error?: string } | null)?.error;
            if (error || bodyError) {
                if (error) reportError(error, 'additional-work-panel.send-quote-email');
                toast.error(bodyError || errorMessage(error, 'Could not send. Please try again.'));
                return;
            }
            toast.success(r.state === 'sent' ? 'Sent again.' : 'Sent to the customer for approval.');
            changed();
        } finally {
            setBusyId(null);
        }
    };

    const cancel = async (r: AdditionalWorkRequest) => {
        const ok = await confirm({
            title: `Withdraw extra work #${r.sequence_no}?`,
            message: r.state === 'sent'
                ? 'The customer’s link will say the request is no longer current.'
                : 'The draft is kept in the job history as cancelled.',
            confirmText: 'Withdraw',
        });
        if (!ok) return;
        setBusyId(r.id);
        try {
            const { error } = await supabase.rpc('fn_additional_work_cancel', { p_quote_id: r.id, p_reason: null });
            if (error) { reportError(error, 'additional-work-panel.fn-additional-work-cancel'); toast.error(errorMessage(error)); return; }
            changed();
        } finally {
            setBusyId(null);
        }
    };

    const copyLink = async (r: AdditionalWorkRequest) => {
        const url = `${window.location.origin}/quote/${r.public_token}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Approval link copied.');
        } catch {
            toast(url);
        }
    };

    if (loading) {
        return (
            <div className="rounded-xl border border-line p-4 flex items-center gap-2 text-xs text-fg-4">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading approved work…
            </div>
        );
    }
    if (!auth) return null;

    const requests = visibleRequests(auth.additional_work);
    const blocked = !!auth.blocked_reason;
    const pending = requests.filter((r) => r.state === 'sent').length;
    // Both figures are the server's; this only compares them, in pence.
    const currentTotal = Number(auth.current_total ?? auth.approved_total);
    const drift = Math.round((currentTotal - Number(auth.approved_total)) * 100) / 100;

    return (
        <section
            className="rounded-xl border border-line bg-card overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            aria-label="Approved work"
        >
            <header className="flex items-center gap-2 px-4 pt-4">
                <ShieldCheck className="w-4 h-4 text-gold shrink-0" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-fg-3">Approved work</h4>
            </header>

            <div className="px-4 py-3 space-y-2">
                <Row
                    label={auth.original?.from_job_items ? 'Original work' : 'Original approved quote'}
                    hint={auth.original?.from_job_items ? 'No quote — the job’s own items' : undefined}
                    amount={auth.original?.amount ?? 0}
                />

                {requests.map((r) => {
                    const actions = canPriceWork ? requestActions(r, blocked) : requestActions(r, true);
                    const struck = r.state === 'declined' || r.state === 'cancelled';
                    return (
                        <div key={r.id} className="rounded-lg bg-canvas/40 border border-line p-3 min-w-0">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-semibold text-fg">Extra work #{r.sequence_no}</span>
                                        <span className={`px-2 py-0.5 rounded-md text-fd-3xs font-bold uppercase tracking-wider border ${TONE_CLASS[STATE_TONE[r.state]]}`}>
                                            {STATE_LABEL[r.state]}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-fg-3 break-words">
                                        {r.line_items.map((l) => l.description).join(' · ')}
                                    </p>
                                    {r.state === 'declined' && r.rejection_reason && (
                                        <p className="mt-1 text-xs text-danger/80 break-words">“{r.rejection_reason}”</p>
                                    )}
                                </div>
                                <span className={`font-mono text-sm font-bold shrink-0 ${struck ? 'line-through text-fg-4' : r.state === 'accepted' ? 'text-success' : 'text-fg'}`}>
                                    {money(r.amount)}
                                </span>
                            </div>

                            {(actions.edit || actions.send || actions.revise || actions.cancel || r.state === 'sent') && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {actions.edit && (
                                        <ActionButton icon={Pencil} label="Edit" onClick={() => setSheet({ mode: 'edit', request: r })} disabled={busyId === r.id} />
                                    )}
                                    {actions.send && (
                                        <ActionButton
                                            icon={busyId === r.id ? Loader2 : Send}
                                            spin={busyId === r.id}
                                            label={r.state === 'sent' ? 'Resend' : 'Send for approval'}
                                            primary={r.state === 'draft'}
                                            onClick={() => send(r)}
                                            disabled={busyId === r.id}
                                        />
                                    )}
                                    {actions.revise && (
                                        <ActionButton icon={RefreshCw} label="Revise" onClick={() => setSheet({ mode: 'revise', request: r })} disabled={busyId === r.id} />
                                    )}
                                    {r.state === 'sent' && (
                                        <ActionButton icon={Copy} label="Copy link" onClick={() => copyLink(r)} />
                                    )}
                                    {actions.cancel && (
                                        <ActionButton icon={XCircle} label="Withdraw" onClick={() => cancel(r)} disabled={busyId === r.id} />
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="flex items-baseline justify-between gap-3 pt-2 border-t border-line">
                    <span className="text-sm font-bold text-fg">Approved job total</span>
                    <span className="font-mono text-[1rem] font-bold text-gold">{money(auth.approved_total)}</span>
                </div>
                {drift !== 0 && (
                    <div className="space-y-1">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm text-fg">Job total now</span>
                            <span className="font-mono text-sm font-bold text-fg shrink-0">{money(auth.current_total)}</span>
                        </div>
                        <p className={`text-xs ${drift > 0 ? 'text-gold' : 'text-fg-4'}`}>
                            {drift < 0
                                ? `${money(-drift)} under what the customer approved. The invoice uses the job’s lines as they are now.`
                                : `${money(drift)} more than the customer approved. Send it as extra work, or agree it with them before invoicing.`}
                        </p>
                    </div>
                )}
                {pending > 0 && (
                    <p className="text-xs text-fg-4">
                        {money(auth.awaiting_customer)} awaiting the customer — not included until they accept.
                        {!blocked && ' Invoicing the job now closes any unanswered request.'}
                    </p>
                )}
                {blocked && requests.length > 0 && (
                    <p className="text-xs text-fg-4">{auth.blocked_reason} No more extra work can be added or approved.</p>
                )}
            </div>

            {canPriceWork && !blocked && (
                <div className="px-4 pb-4">
                    <button
                        type="button"
                        onClick={() => setSheet({ mode: 'new' })}
                        className="w-full min-h-[48px] flex items-center justify-center gap-2 rounded-lg border border-dashed border-gold/45 text-gold hover:bg-gold-fill/10 font-bold font-display text-sm transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add extra work
                    </button>
                </div>
            )}

            {sheet && (
                <AdditionalWorkSheet
                    key={sheet.request?.id ?? 'new'}
                    isOpen
                    onClose={() => setSheet(null)}
                    onDone={() => { setSheet(null); changed(); }}
                    jobId={jobId}
                    orgId={orgId}
                    // From fn_get_job_authorisation, which a mechanic can read --
                    // fn_get_job_financials is owner/admin only.
                    vatRate={Number(auth.vat_rate) || 0}
                    currentTotal={currentTotal}
                    mode={sheet.mode}
                    existing={sheet.request ?? null}
                />
            )}
        </section>
    );
}

function Row({ label, hint, amount }: { label: string; hint?: string; amount: number }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm text-fg">{label}</p>
                {hint && <p className="text-fd-3xs text-fg-4">{hint}</p>}
            </div>
            <span className="font-mono text-sm font-bold text-fg shrink-0">{money(amount)}</span>
        </div>
    );
}

function ActionButton({ icon: Icon, label, onClick, disabled, primary, spin }: {
    icon: React.ElementType; label: string; onClick: () => void; disabled?: boolean; primary?: boolean; spin?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`min-h-[44px] px-3 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${primary
                ? 'fd-btn-primary'
                : 'bg-raised text-fg-3 hover:text-fg border border-line'}`}
        >
            <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''}`} />
            {label}
        </button>
    );
}
